import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import * as ozon from "@/api/ozonSupply";
import { extractErrorMessage } from "@/lib/api-errors";
import { int0 } from "../format";
import { putDirection } from "./ozonQueries";

/** Действия над направлением: черновик, состав, тип и склад, заявка и её отмена.
 *
 *  Один набор на список и на страницу поставки — вместе с текстами тостов. Разъедься они, один и
 *  тот же вызов на двух экранах отвечал бы по-разному, а половина этих текстов и есть ответ
 *  площадки, который человеку надо прочитать.
 */
/** Выбор типа поставки и пункта сдачи. `dropOffName` — у точки НЕ из списка `/drop-offs` (ПВЗ из
 *  поиска) обязателен: по нему бэкенд находит её у площадки. */
export interface KindPick {
  id: number;
  kind: string;
  dropOff: number | null;
  dropOffName?: string | null;
}

export interface DirectionActions {
  draft: (shipmentId: number) => void;
  order: (v: { id: number; slot: ozon.OzonTimeslot }) => void;
  cancel: (shipmentId: number) => void;
  /** сверить с площадкой заявку, ответа по которой не было (стадия «заявка отправлена») */
  resolve: (shipmentId: number) => void;
  kind: (v: KindPick) => void;
  warehouse: (v: { id: number; warehouse: number | null }) => void;
  content: (v: { id: number; qty: Record<string, number> }) => void;
  /** Идёт ли сейчас вызов ПО ЭТОМУ направлению: в списке их два десятка, и гасить надо одну строку. */
  busy: (shipmentId: number) => boolean;
}

export function useOzonDirectionActions(): DirectionActions {
  const qc = useQueryClient();
  // ручки возвращают ПОЛНОЕ направление — кладём его в кэш вместо перекачивания всего списка:
  // направлений два десятка, у каждого свои строки, и бэкенд собирал бы их заново на каждый клик
  const put = (d: ozon.OzonDirection): void => putDirection(qc, d);

  const orderMut = useMutation({
    mutationFn: (v: { id: number; slot: ozon.OzonTimeslot }) =>
      ozon.createOzonOrder(v.id, v.slot),
    onSuccess: (d) => {
      put(d);
      toast.success(
        `${d.region}: заявка создана, поставка ${d.supply_number ?? d.order_id ?? ""}`,
      );
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Заявка не создана")),
  });

  const cancelMut = useMutation({
    mutationFn: (id: number) => ozon.cancelOzonOrder(id),
    onSuccess: (d) => {
      put(d);
      toast.success(
        `${d.region}: заявка отменена — можно переиграть тип или пункт сдачи и спросить заново`,
      );
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Заявку не отменили")),
  });

  // ⚠️ Сверка отвечает ТРЕМЯ разными исходами, и каждый надо назвать своими словами: молча
  // показать «готово» после того, как направление вернулось в «черновик снят» (заявки на
  // площадке нет), значит оставить человека с мыслью, что поставка создана.
  const resolveMut = useMutation({
    mutationFn: (id: number) => ozon.resolveOzonOrdering(id),
    onSuccess: (d) => {
      put(d);
      if (d.state === "ordered")
        toast.success(
          `${d.region}: заявка ${d.supply_number ?? d.order_id ?? ""} нашлась на площадке`,
        );
      else if (d.state === "ordering")
        toast.info(`${d.region}: ${d.note ?? "площадка ещё не ответила"}`);
      else
        toast.warning(
          `${d.region}: заявки на площадке нет — ${d.note ?? "создайте её заново"}`,
        );
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Сверка не удалась")),
  });

  const contentMut = useMutation({
    mutationFn: (v: { id: number; qty: Record<string, number> }) =>
      ozon.setOzonContent(v.id, v.qty),
    onSuccess: (d) => {
      put(d);
      if (d.state === "refused")
        toast.warning(`${d.region}: с новым составом площадка сюда не везёт`);
      else
        toast.success(
          `${d.region}: состав изменён, площадка переспрошена — возьмут ${int0(d.accepted)}`,
        );
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Состав не изменён")),
  });

  const kindMut = useMutation({
    mutationFn: (v: KindPick) =>
      ozon.setOzonSupplyKind(v.id, v.kind, v.dropOff, v.dropOffName),
    onSuccess: (d) => {
      put(d);
      if (d.state === "")
        toast.info(`${d.region}: тип изменён — спросите площадку заново`);
    },
    onError: (e) =>
      toast.error(extractErrorMessage(e, "Тип поставки не изменён")),
  });

  const warehouseMut = useMutation({
    mutationFn: (v: { id: number; warehouse: number | null }) =>
      ozon.setOzonWarehouse(v.id, v.warehouse),
    onSuccess: (d) => {
      put(d);
      toast.success(
        d.warehouse
          ? `${d.region}: везём в ${d.warehouse}`
          : `${d.region}: склад по умолчанию`,
      );
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Склад не выбран")),
  });

  const draftMut = useMutation({
    mutationFn: (shipmentId: number) => ozon.draftOzonDirection(shipmentId),
    onSuccess: (d) => {
      put(d);
      // сначала СОСТОЯНИЕ, потом числа: у сорвавшегося вызова «взяли 0» — не новость об отказе
      // площадки, а отсутствие ответа, и «берут всё (0)» тут было прямой ложью
      if (d.state === "failed")
        toast.error(`${d.region}: ${d.note ?? "площадка не ответила"}`);
      else if (d.state === "refused")
        toast.warning(`${d.region}: площадка сюда не везёт`);
      else if (d.dropped > 0)
        toast.warning(
          `${d.region}: возьмут ${int0(d.accepted)} из ${int0(d.requested)}`,
        );
      else toast.success(`${d.region}: берут всё (${int0(d.accepted)})`);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Площадка не ответила")),
  });

  return {
    draft: draftMut.mutate,
    order: orderMut.mutate,
    cancel: cancelMut.mutate,
    resolve: resolveMut.mutate,
    kind: kindMut.mutate,
    warehouse: warehouseMut.mutate,
    content: contentMut.mutate,
    busy: (id) =>
      (draftMut.isPending && draftMut.variables === id) ||
      (contentMut.isPending && contentMut.variables?.id === id) ||
      (kindMut.isPending && kindMut.variables?.id === id) ||
      (warehouseMut.isPending && warehouseMut.variables?.id === id) ||
      (orderMut.isPending && orderMut.variables?.id === id) ||
      (cancelMut.isPending && cancelMut.variables === id) ||
      (resolveMut.isPending && resolveMut.variables === id),
  };
}
